import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Check, Loader2 } from "lucide-react";
import { Header, Footer } from "../components/Layout";
import { VEHICLES } from "../data/vehicles";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Animation variants - extracted to prevent re-renders
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerChildren = {
  visible: { transition: { staggerChildren: 0.1 } }
};

// Specs Grid Component
const SpecsGrid = ({ specs }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-12">
    {Object.entries(specs).slice(0, 6).map(([key, value]) => (
      <div key={key} className="border border-[#262626] p-4 md:p-6">
        <p className="font-display text-2xl md:text-3xl text-[#FF2A00] mb-1">{value}</p>
        <p className="font-mono text-xs uppercase tracking-wider text-[#A3A3A3]">
          {key.replace(/([A-Z])/g, ' $1').trim()}
        </p>
      </div>
    ))}
  </div>
);

// Variants Display Component
const VariantsDisplay = ({ variants }) => (
  <div className="mb-12">
    <h3 className="font-display text-2xl mb-6">AVAILABLE VARIANTS</h3>
    <div className="grid md:grid-cols-3 gap-4">
      {variants.map((v) => (
        <div key={v.name} className="border border-[#262626] p-6 hover:border-[#FF2A00] transition-colors">
          <p className="font-display text-xl mb-2">{v.name}</p>
          <p className="font-mono text-lg text-[#FF2A00] mb-2">{v.price}</p>
          <p className="text-sm text-[#A3A3A3]">{v.power || v.description}</p>
        </div>
      ))}
    </div>
  </div>
);

// Features List Component
const FeaturesList = ({ features }) => (
  <div>
    <h3 className="font-display text-2xl mb-6">FEATURES</h3>
    <ul className="grid md:grid-cols-2 gap-3">
      {features.map((feature) => (
        <li key={feature} className="flex items-center gap-3 text-[#A3A3A3]">
          <Check className="w-4 h-4 text-[#FF2A00] flex-shrink-0" />
          {feature}
        </li>
      ))}
    </ul>
  </div>
);

// Gallery Component
const VehicleGallery = ({ images, name }) => {
  if (images.length <= 1) return null;
  
  return (
    <div className="mt-16 md:mt-24">
      <h3 className="font-display text-2xl mb-6">GALLERY</h3>
      <div className="grid md:grid-cols-2 gap-4">
        {images.slice(1).map((img) => (
          <div key={img} className="border border-[#262626] overflow-hidden">
            <img src={img} alt={name} className="w-full h-auto" />
          </div>
        ))}
      </div>
    </div>
  );
};

// Helper to get default variant
const getDefaultVariant = (vehicleId, vehicle) => {
  if (vehicle.variants) {
    return vehicle.variants[1]?.name || vehicle.variants[0]?.name || "";
  }
  if (["adler", "falke", "quelle"].includes(vehicleId)) {
    return "h-pattern";
  }
  return "";
};

// Reservation Form Component
const ReservationForm = ({ vehicleId, vehicle, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(() => ({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    country: "",
    vehicle: vehicleId,
    variant: getDefaultVariant(vehicleId, vehicle),
    notes: ""
  }));

  // Update vehicle and variant when props change
  useEffect(() => {
    const newVariant = getDefaultVariant(vehicleId, vehicle);
    setFormData(prev => ({ 
      ...prev, 
      variant: newVariant, 
      vehicle: vehicleId 
    }));
  }, [vehicleId, vehicle]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleVariantChange = useCallback((value) => {
    setFormData(prev => ({ ...prev, variant: value }));
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    
    try {
      const res = await axios.post(`${API}/reservations`, formData);
      toast.success("Reservation submitted successfully!");
      onSuccess(res.data?.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit reservation. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [formData, onSuccess]);

  const showTransmissionOptions = ["adler", "falke", "quelle"].includes(vehicleId);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="font-mono text-xs uppercase tracking-wider">First Name *</Label>
          <Input
            name="first_name"
            value={formData.first_name}
            onChange={handleInputChange}
            className="bg-[#0F0F0F] border-[#262626] rounded-none h-12 mt-1"
            required
            data-testid="first-name-input"
          />
        </div>
        <div>
          <Label className="font-mono text-xs uppercase tracking-wider">Last Name *</Label>
          <Input
            name="last_name"
            value={formData.last_name}
            onChange={handleInputChange}
            className="bg-[#0F0F0F] border-[#262626] rounded-none h-12 mt-1"
            required
            data-testid="last-name-input"
          />
        </div>
      </div>

      <div>
        <Label className="font-mono text-xs uppercase tracking-wider">Email *</Label>
        <Input
          name="email"
          type="email"
          value={formData.email}
          onChange={handleInputChange}
          className="bg-[#0F0F0F] border-[#262626] rounded-none h-12 mt-1"
          required
          data-testid="email-input"
        />
      </div>

      <div>
        <Label className="font-mono text-xs uppercase tracking-wider">Phone *</Label>
        <Input
          name="phone"
          type="tel"
          value={formData.phone}
          onChange={handleInputChange}
          className="bg-[#0F0F0F] border-[#262626] rounded-none h-12 mt-1"
          required
          data-testid="phone-input"
        />
      </div>

      <div>
        <Label className="font-mono text-xs uppercase tracking-wider">Country</Label>
        <Input
          name="country"
          value={formData.country}
          onChange={handleInputChange}
          className="bg-[#0F0F0F] border-[#262626] rounded-none h-12 mt-1"
          data-testid="country-input"
        />
      </div>

      {showTransmissionOptions && (
        <TransmissionOptions value={formData.variant} onChange={handleVariantChange} />
      )}

      {vehicle.variants && (
        <VariantOptions variants={vehicle.variants} value={formData.variant} onChange={handleVariantChange} />
      )}

      <div>
        <Label className="font-mono text-xs uppercase tracking-wider">Notes</Label>
        <Textarea
          name="notes"
          value={formData.notes}
          onChange={handleInputChange}
          className="bg-[#0F0F0F] border-[#262626] rounded-none mt-1 min-h-[80px]"
          placeholder="Any questions or preferences..."
          data-testid="notes-textarea"
        />
      </div>

      <SubmitButton loading={loading} />
    </form>
  );
};

// Transmission Options Component
const TransmissionOptions = ({ value, onChange }) => (
  <div>
    <Label className="font-mono text-xs uppercase tracking-wider mb-2 block">Transmission</Label>
    <RadioGroup value={value} onValueChange={onChange} className="flex gap-4">
      <div className="flex items-center gap-2">
        <RadioGroupItem value="h-pattern" id="h-pattern" />
        <Label htmlFor="h-pattern" className="text-sm cursor-pointer">H-Pattern</Label>
      </div>
      <div className="flex items-center gap-2">
        <RadioGroupItem value="sequential" id="sequential" />
        <Label htmlFor="sequential" className="text-sm cursor-pointer">Sequential</Label>
      </div>
    </RadioGroup>
  </div>
);

// Variant Options Component
const VariantOptions = ({ variants, value, onChange }) => (
  <div>
    <Label className="font-mono text-xs uppercase tracking-wider mb-2 block">Model</Label>
    <RadioGroup value={value} onValueChange={onChange} className="space-y-2">
      {variants.map((v) => (
        <div key={v.name} className="flex items-center gap-2">
          <RadioGroupItem value={v.name} id={v.name} />
          <Label htmlFor={v.name} className="text-sm cursor-pointer">
            {v.name} — {v.price}
          </Label>
        </div>
      ))}
    </RadioGroup>
  </div>
);

// Submit Button Component
const SubmitButton = ({ loading }) => (
  <>
    <button 
      type="submit"
      disabled={loading}
      className="w-full font-mono text-sm uppercase tracking-wider bg-[#FF2A00] text-white py-4 hover:bg-white hover:text-black transition-colors duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
      data-testid="submit-reservation-btn"
    >
      {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</> : "Submit Reservation"}
    </button>
    <p className="text-xs text-[#A3A3A3] text-center">
      No payment required. We'll contact you with details.
    </p>
  </>
);

// Reservation Sidebar Component
const ReservationSidebar = ({ vehicle, vehicleId }) => {
  const [showForm, setShowForm] = useState(false);
  const navigate = useNavigate();

  const handleSuccess = useCallback((reservationId) => {
    setShowForm(false);
    const params = new URLSearchParams({ vehicle: vehicleId });
    if (reservationId) params.set("id", reservationId);
    navigate(`/success?${params.toString()}`);
  }, [navigate, vehicleId]);

  const handleShowForm = useCallback(() => {
    setShowForm(true);
  }, []);

  return (
    <div className="sticky top-24">
      <div className="border border-[#262626] p-6 md:p-8">
        <p className="font-mono text-xs uppercase tracking-wider text-[#A3A3A3] mb-2">Starting at</p>
        <p className="font-display text-4xl text-[#FF2A00] mb-2">{vehicle.price}</p>
        {vehicle.priceNote && (
          <p className="text-sm text-[#A3A3A3] mb-6">{vehicle.priceNote}</p>
        )}

        <div className="flex items-center gap-2 text-sm text-[#A3A3A3] mb-6">
          <Clock className="w-4 h-4" />
          <span>{vehicle.timeline}</span>
        </div>

        {!showForm ? (
          <button 
            onClick={handleShowForm}
            className="w-full font-mono text-sm uppercase tracking-wider bg-[#FF2A00] text-white py-4 hover:bg-white hover:text-black transition-colors duration-200"
            data-testid="reserve-btn"
          >
            Reserve Now
          </button>
        ) : (
          <ReservationForm 
            vehicleId={vehicleId} 
            vehicle={vehicle} 
            onSuccess={handleSuccess}
          />
        )}
      </div>
    </div>
  );
};

// Vehicle Hero Component
const VehicleHero = ({ vehicle }) => (
  <section className="relative pt-20" data-testid="vehicle-detail-hero">
    <div className="absolute top-24 left-6 md:left-8 z-20">
      <Link to="/vehicles" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-white transition-colors">
        <ArrowLeft className="w-4 h-4" /> All Vehicles
      </Link>
    </div>

    <div className="relative h-[50vh] md:h-[70vh] overflow-hidden">
      {vehicle.images[0] ? (
        <img 
          src={vehicle.images[0]}
          alt={vehicle.name}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full bg-[#0F0F0F] flex items-center justify-center">
          <p className="font-mono text-sm uppercase tracking-wider text-[#A3A3A3]">Design in Progress</p>
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/30 to-transparent" />
      
      <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
        <div className="max-w-[1400px] mx-auto">
          <p className="font-mono text-xs md:text-sm tracking-[0.2em] uppercase text-[#FF2A00] mb-2">{vehicle.edition}</p>
          <h1 className="font-display text-6xl md:text-8xl lg:text-9xl tracking-tight">{vehicle.name}</h1>
        </div>
      </div>
    </div>
  </section>
);

// Vehicle Content Component
const VehicleContent = ({ vehicle, vehicleId }) => (
  <section className="py-16 md:py-24 px-6 md:px-8" data-testid="vehicle-detail-content">
    <div className="max-w-[1400px] mx-auto">
      <div className="grid lg:grid-cols-3 gap-12 lg:gap-16">
        <div className="lg:col-span-2">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerChildren}
          >
            <motion.p variants={fadeInUp} className="text-lg md:text-xl text-[#A3A3A3] leading-relaxed mb-8">
              {vehicle.description}
            </motion.p>

            <motion.div variants={fadeInUp}>
              <SpecsGrid specs={vehicle.specs} />
            </motion.div>

            {vehicle.variants && (
              <motion.div variants={fadeInUp}>
                <VariantsDisplay variants={vehicle.variants} />
              </motion.div>
            )}

            <motion.div variants={fadeInUp}>
              <FeaturesList features={vehicle.features} />
            </motion.div>
          </motion.div>
        </div>

        <div className="lg:col-span-1">
          <ReservationSidebar vehicle={vehicle} vehicleId={vehicleId} />
        </div>
      </div>

      <VehicleGallery images={vehicle.images} name={vehicle.name} />
    </div>
  </section>
);

// Main Vehicle Detail Page
export default function VehicleDetailPage() {
  const { vehicleId } = useParams();
  const vehicle = VEHICLES[vehicleId];

  if (!vehicle) {
    return (
      <main className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <p className="font-display text-4xl mb-4">Vehicle Not Found</p>
          <Link to="/vehicles" className="text-[#FF2A00] hover:underline">View All Vehicles</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#050505]">
      <Header />
      <VehicleHero vehicle={vehicle} />
      <VehicleContent vehicle={vehicle} vehicleId={vehicleId} />
      <Footer />
    </main>
  );
}
